<template>
  <div class="documentos-cartoes">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-file-pdf"></i>
        Importar Cartões Resposta Corrigidos
      </h1>
      <p class="page-subtitle">
        Envie em lote os PDFs de cartões resposta corrigidos para o sistema, vinculando pelo ID da inscrição no nome do arquivo.
      </p>
    </div>

    <div class="card">
      <div class="card-body">
        <div class="form-grid">
          <div class="form-group">
            <label>Pasta local com PDFs *</label>
            <input
              ref="inputPasta"
              type="file"
              class="form-control"
              webkitdirectory
              multiple
              @change="onFolderChange"
            />
            <small class="form-text text-muted">
              Selecione a pasta onde estão os PDFs. Cada arquivo deve estar nomeado com o ID da inscrição,
              por exemplo: <code>283.pdf</code>, <code>1502.pdf</code>.
            </small>
            <div v-if="arquivosSelecionados.length" class="mt-1 small text-muted">
              {{ arquivosSelecionados.length }} arquivo(s) selecionado(s).
            </div>
          </div>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label>Título padrão do documento</label>
            <input
              v-model="form.titulo"
              type="text"
              class="form-control"
              placeholder="Cartão Resposta"
            />
          </div>

          <div class="form-group">
            <label>Tipo do documento</label>
            <input
              v-model="form.tipo"
              type="text"
              class="form-control"
              placeholder="cartao_resposta"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Descrição padrão</label>
          <textarea
            v-model="form.descricao"
            class="form-control"
            rows="2"
            placeholder="Cartão resposta processado."
          ></textarea>
        </div>

        <div class="actions">
          <button
            class="btn btn-primary"
            :disabled="processing || !arquivosSelecionados.length"
            @click="processarCartoes"
          >
            <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-upload-alt'"></i>
            {{ processing ? 'Processando...' : 'Enviar cartões para o sistema' }}
          </button>
        </div>

        <div v-if="resultado" class="resultado mt-4">
          <div class="alert alert-success" v-if="resultado.processados > 0">
            <strong>{{ resultado.processados }}</strong> de {{ resultado.total_arquivos }} arquivo(s) processados com sucesso.
          </div>
          <div class="alert alert-warning" v-else>
            Nenhum arquivo foi processado com sucesso.
          </div>

          <div v-if="resultado.erros && resultado.erros.length" class="erros-list mt-3">
            <h4>
              <i class="fas fa-exclamation-triangle text-danger"></i>
              Arquivos com erro ({{ resultado.erros.length }})
            </h4>
            <ul>
              <li v-for="(erro, index) in resultado.erros" :key="index">
                <strong>{{ erro.arquivo }}</strong> - {{ erro.motivo }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DocumentosCartoes',
  data() {
    return {
      processing: false,
      resultado: null,
      arquivosSelecionados: [],
      form: {
        tipo: 'cartao_resposta',
        titulo: 'Cartão Resposta',
        descricao: 'Cartão resposta processado.'
      }
    }
  },
  methods: {
    async processarCartoes() {
      if (!this.arquivosSelecionados.length) {
        this.$sweet?.sweetMensagem?.('Selecione a pasta com os PDFs.')
        return
      }

      this.processing = true
      this.resultado = null

      try {
        const formData = new FormData()
        if (this.form.tipo) formData.append('tipo', this.form.tipo)
        if (this.form.titulo) formData.append('titulo', this.form.titulo)
        if (this.form.descricao) formData.append('descricao', this.form.descricao)

        this.arquivosSelecionados.forEach((file) => {
          formData.append('arquivos[]', file)
        })

        const { data } = await axios.post('/ps/documento-inscricao/admin/processar-cartoes-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        this.resultado = data
        this.$sweet?.sweetSuccess?.(data.message || 'Processamento concluído.')
      } catch (error) {
        console.error('Erro ao processar cartões em lote:', error)
        const msg = error.response?.data?.error || error.response?.data?.message || 'Erro ao processar cartões.'
        this.$sweet?.sweetError?.(msg)
      } finally {
        this.processing = false
      }
    },
    onFolderChange(event) {
      const files = Array.from(event.target.files || [])
      // filtrar só PDFs
      this.arquivosSelecionados = files.filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))
    }
  }
}
</script>

<style lang="scss" scoped>
.documentos-cartoes {
  .page-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .page-subtitle {
      margin-top: 5px;
      color: #6c757d;
    }
  }

  .card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
    padding: 20px 24px;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 20px;
    margin-bottom: 15px;
  }

  .form-group {
    label {
      font-weight: 600;
      margin-bottom: 6px;
      display: block;
    }

    .form-text {
      font-size: 12px;
    }
  }

  .actions {
    margin-top: 10px;
    display: flex;
    justify-content: flex-start;
  }

  .resultado {
    .alert {
      padding: 12px 16px;
      border-radius: 6px;
      margin-bottom: 10px;
    }

    .alert-success {
      background-color: #d4edda;
      color: #155724;
    }

    .alert-warning {
      background-color: #fff3cd;
      color: #856404;
    }

    .erros-list {
      h4 {
        font-size: 16px;
        margin-bottom: 8px;
      }

      ul {
        padding-left: 18px;
        margin: 0;
      }

      li {
        font-size: 13px;
        margin-bottom: 4px;
      }
    }
  }
}
</style>


